import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Badge, Button, IconHighlight, ModalLayout, SearchInput } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';

const TITLE_ID = 'assign-item-title';

/**
 * Pick one item for a member (Assign role, Assign group): a search box over the items that are
 * still free, and a click on one assigns it and closes. The prototype's modal for both.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {string} props.searchPlaceholder Also the accessible name of the search box.
 * @param {string} props.emptyMessage Shown when there is nothing left to assign.
 * @param {string} props.noMatchMessage Shown when the search matches nothing.
 * @param {{ id: string, name: string, description?: string, detail?: string, badge?: string }[]} props.items
 * @param {(item: object) => import('react').ReactNode} [props.renderLeading] Avatar before the name.
 * @param {() => void} props.onClose
 * @param {(itemId: string) => Promise<boolean>} props.onPick Resolves `true` when assigned (the modal closes).
 */
function AssignItemBody({
  title,
  subtitle,
  searchPlaceholder,
  emptyMessage,
  noMatchMessage,
  items,
  renderLeading,
  onClose,
  onPick,
}) {
  const { t } = useTranslation('people-access');
  const [query, setQuery] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const pendingRef = useRef(false);
  const handleClose = useCallback(() => {
    if (!pendingRef.current) onClose();
  }, [onClose]);
  useDialogKeyboard(true, handleClose, TITLE_ID);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => `${item.name} ${item.description ?? ''}`.toLowerCase().includes(needle));
  }, [items, query]);

  const handlePick = useCallback(
    async (itemId) => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      setPendingId(itemId);
      let ok = false;
      try {
        ok = await onPick(itemId);
      } finally {
        pendingRef.current = false;
      }
      // On success the modal closes as it is: no frame with everything enabled again.
      if (ok) onClose();
      else setPendingId(null);
    },
    [onPick, onClose],
  );

  return (
    <ModalLayout
      isOpen
      onClose={handleClose}
      titleId={TITLE_ID}
      closeAriaLabel={t('common.close')}
      closeDisabled={pendingId !== null}
      useBodyPortal
      titlePrefix={<IconHighlight icon={Plus} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      title={title}
      subtitle={subtitle}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName={WIDE_MODAL}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <SearchInput
          autoFocus
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={query}
          readOnly={pendingId !== null}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery('')}
        />
        <div className="scrollbar-aimsos-auto-sm max-h-72 overflow-y-auto">
          {visible.length === 0 ? (
            <p className="py-6 text-center text-sm text-content-muted">
              {items.length === 0 ? emptyMessage : noMatchMessage}
            </p>
          ) : (
            <ul className="flex flex-col">
              {visible.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    aria-disabled={pendingId !== null}
                    onClick={() => handlePick(item.id)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 aria-disabled:cursor-wait aria-disabled:opacity-60"
                  >
                    {renderLeading ? renderLeading(item) : null}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-content">{item.name}</span>
                      {item.description || item.detail ? (
                        <span className="truncate text-xs text-content-muted">{item.description || item.detail}</span>
                      ) : null}
                    </span>
                    {item.badge ? <Badge variant="neutral">{item.badge}</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="flex justify-end border-t border-edge px-6 py-4">
        <Button variant="secondary" size="md" disabled={pendingId !== null} onClick={handleClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </ModalLayout>
  );
}

/** The body mounts only while open, so the search and the pending state start fresh every time. */
function AssignItemModal({ isOpen, ...props }) {
  return isOpen ? <AssignItemBody {...props} /> : null;
}

export default memo(AssignItemModal);
